import React, { useCallback, useEffect, useState, useContext } from 'react';
import { Globe, Search, RefreshCw, AlertTriangle, Shield, Download, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { useLoading } from '../../hooks/useLoading';
import { useTeams } from '../../contexts/TeamsContext';
import { getApiConfiguration } from '../../api/utils';
import { BrandApi, DomainThreat , DomainThreatStats } from 'api-client';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { TablePagination } from '../../components/ui/TablePagination';


interface DomainStats {
  total_threats: number;
  by_type: Array<{
    threat_type: string;
    count: number;
  }>;
  by_status: Array<{
    status: string;
    count: number;
  }>;
}

const DomainMonitoring = () => {
  const { isLoading, withLoading } = useLoading();
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [domainThreats, setDomainThreats] = useState<DomainThreat[]>([]);
  const [stats, setStats] = useState<DomainThreatStats | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isDomainThreatsLoading, setIsDomainThreatsLoading] = useState(false);

  const [paginationInfo, setPaginationInfo] = useState<{ count: number; next: string | null; previous: string | null }>(
    { count: 0, next: null, previous: null }
  );

  const fetchStats = useCallback(async () => {
    if (!currentTeam) return;
    
    const api = new BrandApi(getApiConfiguration(token));
    try {
      const stats = await api.brandThreatsStatsRetrieve({
        teamSlug: String(currentTeam.slug),
        id: 1
      })
      setStats(stats);

    } catch (error) {
      console.error('Error fetching domain threat stats:', error);
    }
  }, [currentTeam, token]);

  const fetchDomainThreats = useCallback(async (page = 1) => {
    if (!currentTeam) return;
    
    const api = new BrandApi(getApiConfiguration(token));
    try {
      setIsDomainThreatsLoading(true);
      const response = await api.brandThreatsList({
        teamSlug: String(currentTeam.slug),
        page,
        limit: 3
      });
      setDomainThreats(response.results || []);
      setIsDomainThreatsLoading(false);
      setTotalItems(response.count || 0);
      setPaginationInfo({
        count: response.count || 0,
        next: String(response.next),
        previous: String(response.previous)
      });
    } catch (error) {
      console.error('Error fetching domain threats:', error);
      setIsDomainThreatsLoading(false);
    }
  }, [currentTeam, token]);

  useEffect(() => {
    fetchDomainThreats(currentPage);
    fetchStats();
  }, [fetchDomainThreats, fetchStats, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleScan = async () => {
    await withLoading(async () => {
      await Promise.all([
        fetchDomainThreats(currentPage),
        fetchStats()
      ]);
    });
  };



  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Scanning domains..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Globe className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Domain Monitoring</h1>
            <p className="text-sm text-gray-400">Monitor and protect against domain abuse</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={handleScan}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>

        </div>
      </div>

      <div className="grid grid-cols-4 gap-6">
        {[
          { label: 'Total Threats', value: stats?.total, trend: '+0', icon: Globe },
          { label: 'Active Threats', value: stats?.byStatus.find(t => t.key === 'detected')?.count || 0, trend: '+0', icon: AlertTriangle },
          { label: 'Under Investigation', value:  stats?.byStatus.find(t => t.key === 'investigating')?.count || 0, trend: '+0', icon: Shield },
          { label: 'Taken Down', value: stats?.byStatus.find(t => t.key === 'takendown')?.count || 0, trend: '+0', icon: AlertTriangle }
        ].map((stat) => (
          <div key={stat.label} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">{stat.label}</span>
              <stat.icon className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className={`text-xs ${stat.trend.startsWith('+') ? 'text-red-500' : 'text-emerald-500'}`}>
                {stat.trend}
              </div>
            </div>
          </div>
        ))}
      </div>


            {/* Domain Threats */}


      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Domain Threats</h2>
          <div className="space-y-4">
            {isDomainThreatsLoading && (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
              </div>
            )}
            {!isDomainThreatsLoading && domainThreats.length === 0 && (
              <div className="text-gray-400">No domain threats found.</div>
            )}
            {!isDomainThreatsLoading && domainThreats.length > 0 && (
              domainThreats.map((threat) => (
                <div key={threat.id} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg hover:bg-gray-800/50 transition-colors">
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-lg ${threat.confidenceLevel && threat.confidenceLevel > 80 ? 'bg-red-500/10' :
                        threat.confidenceLevel && threat.confidenceLevel > 50 ? 'bg-orange-500/10' :
                          'bg-yellow-500/10'
                      }`}>
                      <AlertTriangle className={`h-5 w-5 ${threat.confidenceLevel && threat.confidenceLevel > 80 ? 'text-red-500' :
                          threat.confidenceLevel && threat.confidenceLevel > 50 ? 'text-orange-500' :
                            'text-yellow-500'
                        }`} />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-white">{threat.threatDomain}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Type: {threat.threatTypeDisplay} • Domain: {threat.affectedDomain.domainName}
                      </div>

                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex flex-col items-end">
                      {threat.confidenceLevel !== null && (
                        <div className="text-xs text-gray-400 mb-1">
                          Confidence: {threat.confidenceLevel}%
                        </div>
                      )}
                      <span className={`px-2 py-1 text-xs rounded-full ${threat.status === 'takendown' ? 'bg-emerald-500/10 text-emerald-500' :
                          threat.status === 'investigating' ? 'bg-yellow-500/10 text-yellow-500' :
                            threat.status === 'reported' ? 'bg-orange-500/10 text-orange-500' :
                              'bg-blue-500/10 text-blue-500'
                        }`}>
                        {threat.statusDisplay}
                      </span>
                    </div>
                    <Link
                      to={`/brand-protection/threats/${threat.id}`}
                      className="p-2 rounded-lg hover:bg-gray-700/50 transition-colors"
                    >
                      <Eye className="h-4 w-4 text-gray-400" />
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        {!isDomainThreatsLoading && domainThreats.length > 0 && (
          <TablePagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            totalItems={totalItems}
            itemsPerPage={3}
            paginationInfo={paginationInfo}
            itemName="threats"
          />
        )}
      </div>

      {/* Domain Categories */}
      <div className="grid grid-cols-3 gap-6">
        {[
          {
            category: 'Typosquatting',
            count: stats?.byType.find(t => t.key === 'typosquatting')?.count || 0,
            description: 'Domains with similar spelling',
            trend: 'up'
          },
          {
            category: 'Brand Infringement',
            count: stats?.byType.find(t => t.key === 'brand_infringement')?.count || 0,
            description: 'Domains using brand terms',
            trend: 'stable'
          },
          {
            category: 'Phishing Domains',
            count: stats?.byType.find(t => t.key === 'phishing')?.count || 0,
            description: 'Potential phishing attempts',
            trend: 'down'
          }
        ].map((category) => (
          <div key={category.category} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{category.category}</h3>
              <div className={`text-xs ${
                category.trend === 'up' ? 'text-red-500' :
                category.trend === 'down' ? 'text-emerald-500' :
                'text-gray-500'
              }`}>
                {category.trend === 'up' ? '↑' :
                 category.trend === 'down' ? '↓' :
                 '→'}
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-2">{category.count}</div>
            <p className="text-sm text-gray-400">{category.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DomainMonitoring;